/**
 * What somebody was working ON, not which app they had open.
 *
 * "Brave 55m, Code 14m" says nothing about whether yesterday's task got any
 * time today. The window titles, URLs and file paths WorkMonitor already
 * stores do — they carry the repo, the folder, the portal page, the document.
 *
 * Deliberately domain-free. There is no rule here for "accountant" or
 * "developer", because the next hire is in legal or design and that list never
 * finishes. Whatever the job, the activity leaves one of four shapes behind,
 * and they are read in order of how much they actually pin down:
 *
 *   1. a file path   — the folder tree IS the work breakdown, in every domain:
 *                      /Accounts/Acme/GST/Oct, /Legal/Contracts/Acme,
 *                      /Design/Brand/Logo-v3
 *   2. a URL         — host plus the first path segment, for anyone living in
 *                      a web app: a portal, a CRM, Teamlio itself
 *   3. a window title— "file - project - App" is a shape most desktop apps
 *                      share, so the app name is dropped and what is left is
 *                      read as context then detail
 *   4. the app alone — the floor. An app whose title says nothing ("Tally.ERP
 *                      9") cannot be broken down further, and pretending
 *                      otherwise would be inventing detail.
 *
 * Pure: no React, no api client. Tested in src/utils/workAreas.test.ts.
 */

export interface TimelineEntry {
  app_name?: string | null;
  app_exe?: string | null;
  title?: string | null;
  url?: string | null;
  file_path?: string | null;
  duration_mins?: number | null;
  /** "active" / "idle" / "system_event". */
  type?: string | null;
}

export interface WorkItem {
  /** What to group and show: a repo, a folder, a host + section, an app. */
  area: string;
  /** The finer thing inside it, when there is one — a file, a page, a doc. */
  detail: string | null;
  /** Which of the four shapes this came from. */
  source: "file" | "url" | "title" | "app";
}

export interface AreaDelta {
  area: string;
  before: number;
  after: number;
  delta: number;
  /** Up to a handful of the things touched inside the area, later day first. */
  details: string[];
  /** What was open inside this area on the EARLIER day, longest first. */
  beforeDetails: string[];
  /** ...and on the LATER day.
   *
   *  Kept apart from `details` rather than folded into it, because the question
   *  people actually ask is "it was these files on the 13th — which ones is it
   *  today?". One merged list answers a different, duller question: which files
   *  were seen at some point across two days. */
  afterDetails: string[];
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Rows that are not work being done.
 *
 * "system_event" is a marker — a sleep, a wake, a tracking gap.
 * "idle" is time the person was at the machine but not using it, and the
 * compressor writes it with app_name "Idle". Letting it through made Idle
 * itself the third-largest "work area" on the report, which is both wrong and
 * the kind of wrong that makes everything beside it look unreliable.
 */
const NON_WORK_TYPES = new Set(["system_event", "idle"]);
const NON_WORK_APPS = new Set(["idle"]);

/**
 * Below this an area is not something somebody worked on — it is a window that
 * held focus in passing while they alt-tabbed somewhere else.
 *
 * Measured on real days before choosing it: of 29 areas on a 4h29m day, 20 were
 * under two minutes, and of 8 areas on a 9-minute day, 7 were. That is the
 * "random files" a reader sees, and it crowds out the five that are the day.
 * Two minutes on a four-hour day is under one percent of it.
 */
export const MIN_AREA_MINS = 2;

/**
 * Areas that are never work, whatever the time on them.
 *
 * Not a list of apps anyone dislikes — each of these is the machine or this app
 * talking about itself:
 *   "Task Switching"  the Windows Alt-Tab overlay. It appears BECAUSE somebody
 *                     left one thing for another; counting it as a place they
 *                     worked is counting the corridor as a room.
 *   "[DLP] …"         WorkMonitor's own event markers, written by the file and
 *                     clipboard watchers. Our instrumentation, not their work.
 */
const JUNK_AREA_EXACT = new Set(["task switching"]);
const JUNK_AREA_PREFIX = ["[dlp]"];

/**
 * A scratch file an editor or a tool made, not a document anybody opened:
 * "tax.py.tmp.18872.1aee9344f49b". Matched on the shape — a .tmp segment, or a
 * long hex tail — rather than on an extension list, because every tool spells
 * its temporary files differently and the shape is what they share.
 */
const TEMP_FILE = /(\.tmp[.\d]|~\$|\.[0-9a-f]{8,}$)/i;

/** Is this area name worth showing a person at all? */
export function isRealArea(area: string | null | undefined): boolean {
  const name = String(area || "").trim();
  if (!name) return false;
  const lower = name.toLowerCase();
  if (JUNK_AREA_EXACT.has(lower)) return false;
  if (JUNK_AREA_PREFIX.some((p) => lower.startsWith(p))) return false;
  if (TEMP_FILE.test(name)) return false;
  return true;
}

/**
 * Apps that are a window onto something else rather than a thing worked in.
 * Their title is the page's title, which the URL already describes better —
 * and describes consistently, where titles change as the page does.
 */
const BROWSER_EXES = [
  "chrome.exe", "msedge.exe", "firefox.exe", "opera.exe", "brave.exe",
];

/** Windows and POSIX paths, one shape. */
function normalisePath(path: string): string {
  return path.replace(/\\/g, "/").replace(/\/+$/, "");
}

function fromFilePath(raw: string): WorkItem {
  const path = normalisePath(raw.trim());
  const parts = path.split("/").filter(Boolean);
  if (parts.length <= 1) {
    return { area: path, detail: null, source: "file" };
  }
  // The containing folder is the area, the file the detail. A folder on its
  // own (Explorer sitting in a directory) is itself the area.
  const looksLikeFile = /\.[A-Za-z0-9]{1,8}$/.test(parts[parts.length - 1]);
  if (!looksLikeFile) {
    return { area: parts[parts.length - 1], detail: null, source: "file" };
  }
  return {
    area: parts[parts.length - 2],
    detail: parts[parts.length - 1],
    source: "file",
  };
}

function fromUrl(raw: string): WorkItem | null {
  let text = raw.trim();
  if (!text) return null;
  if (!/^[a-zA-Z]+:\/\//.test(text)) text = `http://${text}`;
  let host: string;
  let path: string;
  try {
    const u = new URL(text);
    host = u.host;
    path = u.pathname;
  } catch {
    return null;
  }
  if (!host) return null;
  const segments = path.split("/").filter(Boolean);
  // Host plus the first segment: enough to separate one part of a portal from
  // another ("…/leads" vs "…/invoices") without splitting on every record id.
  const area = segments.length ? `${host}/${segments[0]}` : host;
  const detail = segments.length > 1 ? segments.slice(1).join("/") : null;
  return { area, detail, source: "url" };
}

/**
 * "work_monitor_ui.py - WorkMonitor - Visual Studio Code" -> WorkMonitor /
 * work_monitor_ui.py.
 *
 * The last part of a title is nearly always the app, so it goes. What remains
 * reads outermost-last — the context before the app, the document first — so
 * it is taken from the end. One part left is its own area: "Invoice_Oct.xlsx -
 * Excel" is about the invoice, and there is nothing broader to put it under.
 */
function fromTitle(raw: string): WorkItem | null {
  // Split on dashes only. "|" looked like another separator but it is used
  // INSIDE a title, not around context — "Teamlio | One connected workspace
  // for growing teams - Brave" was being read as area "One connected workspace
  // for growing teams" with Teamlio as the detail, exactly backwards.
  const parts = raw
    .split(/\s[-—–]\s/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.length) return null;
  const withoutApp = parts.length > 1 ? parts.slice(0, -1) : parts;
  if (!withoutApp.length) return null;
  if (withoutApp.length === 1) {
    return { area: withoutApp[0], detail: null, source: "title" };
  }
  const area = withoutApp[withoutApp.length - 1];
  // Editors repeat the filename in brackets — "X.tsx (Index) (X.tsx)".
  const detail = withoutApp[0].replace(/\s*\([^)]*\)/g, "").trim() || null;
  return { area, detail, source: "title" };
}

/** The one work item an entry is about, or null when it is about nothing. */
export function workItemOf(entry: TimelineEntry): WorkItem | null {
  if (!entry) return null;
  if (entry.type && NON_WORK_TYPES.has(entry.type)) return null;
  if (NON_WORK_APPS.has((entry.app_name || "").trim().toLowerCase())) return null;

  const exe = (entry.app_exe || "").toLowerCase();
  const isBrowser = BROWSER_EXES.includes(exe);

  // A browser's URL beats its own file_path, which is never about the page.
  if (!isBrowser && entry.file_path) {
    const item = fromFilePath(String(entry.file_path));
    if (item.area) return item;
  }
  if (entry.url) {
    const item = fromUrl(String(entry.url));
    if (item) return item;
  }
  if (!isBrowser && entry.file_path) {
    const item = fromFilePath(String(entry.file_path));
    if (item.area) return item;
  }
  if (entry.title) {
    const item = fromTitle(String(entry.title));
    if (item) return item;
  }
  if (entry.app_name) {
    return { area: String(entry.app_name), detail: null, source: "app" };
  }
  return null;
}

/** Minutes per area for one day, with the details seen inside each. */
export function areaMinutes(
  timeline: TimelineEntry[] | null | undefined,
): Map<string, { mins: number; details: Map<string, number> }> {
  const out = new Map<string, { mins: number; details: Map<string, number> }>();
  for (const entry of timeline || []) {
    const item = workItemOf(entry);
    if (!item) continue;
    if (!isRealArea(item.area)) continue;
    const mins = num(entry.duration_mins);
    if (mins <= 0) continue;
    const bucket = out.get(item.area) || { mins: 0, details: new Map() };
    bucket.mins += mins;
    if (item.detail && !TEMP_FILE.test(item.detail)) {
      bucket.details.set(item.detail, (bucket.details.get(item.detail) || 0) + mins);
    }
    out.set(item.area, bucket);
  }
  // Minutes are summed across the whole day BEFORE the floor is applied — an
  // area returned to six times for twenty seconds each is still two minutes of
  // work, and dropping each visit separately would lose it.
  for (const [area, bucket] of Array.from(out.entries())) {
    if (bucket.mins < MIN_AREA_MINS) out.delete(area);
  }
  return out;
}

/** Up to `limit` details, longest first. */
function topDetails(details: Map<string, number>, limit = 3): string[] {
  return Array.from(details.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([name]) => name);
}

export interface RankedArea {
  area: string;
  mins: number;
  details: string[];
  /** True when the OTHER day has no time in this area at all. */
  onlyHere: boolean;
}

/**
 * Each day as its own ranked list: where the time went, most first.
 *
 * Side by side rather than as one diff table, because a diff makes you read
 * three numbers to learn what somebody did on one day. Two short lists answer
 * "what was he on then, what is he on now" at a glance, and the comparison is
 * the thing a person is actually good at doing by eye.
 *
 * `onlyHere` still carries the finding a diff gave you — the work that came in
 * and the work that was dropped.
 */
export function splitWorkAreas(
  beforeTimeline: TimelineEntry[] | null | undefined,
  afterTimeline: TimelineEntry[] | null | undefined,
): { before: RankedArea[]; after: RankedArea[] } {
  const a = areaMinutes(beforeTimeline);
  const b = areaMinutes(afterTimeline);

  const rank = (
    own: ReturnType<typeof areaMinutes>,
    other: ReturnType<typeof areaMinutes>,
  ): RankedArea[] =>
    Array.from(own.entries())
      .map(([area, v]) => ({
        area,
        mins: Math.round(v.mins * 100) / 100,
        details: topDetails(v.details),
        onlyHere: !(other.get(area)?.mins),
      }))
      .filter((r) => r.mins > 0)
      .sort((x, y) => y.mins - x.mins || x.area.localeCompare(y.area));

  return { before: rank(a, b), after: rank(b, a) };
}

export interface AreaChange {
  area: string;
  beforeMins: number;
  afterMins: number;
}

/**
 * The two ranked lists as the three questions somebody reading a comparison
 * actually asks: what kept going, what is new, what got no time at all.
 *
 * Worked out here rather than asked of a model, because the minutes ARE the
 * finding. "activity-reports got nothing today" means one thing when it had
 * four hours yesterday and nothing much when it had six minutes — and a bare
 * name under a red heading reads like the first either way.
 */
export function diffWorkAreas(
  before: RankedArea[] | null | undefined,
  after: RankedArea[] | null | undefined,
): { carriedOver: AreaChange[]; cameIn: AreaChange[]; dropped: AreaChange[] } {
  const b = new Map((before || []).map((a) => [a.area, a.mins]));
  const a = new Map((after || []).map((x) => [x.area, x.mins]));

  const carriedOver: AreaChange[] = [];
  const cameIn: AreaChange[] = [];
  const dropped: AreaChange[] = [];

  // Bucketed on MINUTES, not on whether the key exists. Keyed on existence,
  // an area with twenty seconds on the later day counted as "on both days" and
  // then rendered as "4m → —", because anything under a minute rounds to a
  // dash. The heading and the row said opposite things.
  for (const [area, afterMins] of Array.from(a.entries())) {
    if (afterMins <= 0) continue;
    const beforeMins = b.get(area) || 0;
    if (beforeMins <= 0) cameIn.push({ area, beforeMins: 0, afterMins });
    else carriedOver.push({ area, beforeMins, afterMins });
  }
  for (const [area, beforeMins] of Array.from(b.entries())) {
    if (beforeMins <= 0) continue;
    if (!(a.get(area) || 0)) dropped.push({ area, beforeMins, afterMins: 0 });
  }

  // Biggest first, by whichever day answers that bucket's question: where the
  // time is going now for the first two, what was lost for the third.
  carriedOver.sort((x, y) => y.afterMins - x.afterMins || x.area.localeCompare(y.area));
  cameIn.sort((x, y) => y.afterMins - x.afterMins || x.area.localeCompare(y.area));
  dropped.sort((x, y) => y.beforeMins - x.beforeMins || x.area.localeCompare(y.area));
  return { carriedOver, cameIn, dropped };
}

const titleCase = (s: string): string =>
  s.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim()
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");

/**
 * An area name as something to read rather than something to decode.
 *
 * Areas are built from window titles and URLs, so they arrive as
 * "localhost:3000/activity-reports" or a page title with its strapline still
 * attached. That is fine for grouping and poor for a person scanning a column
 * of them. Only the presentation changes — the area key itself is untouched,
 * so grouping, diffing and the tables underneath still line up.
 *
 * Deliberately timid: a name that is already a plain word (a repo, an app) is
 * returned exactly as it came, and anything the rules would reduce to nothing
 * is left alone rather than blanked.
 */
export function prettyArea(area: string | null | undefined): string {
  const raw = String(area || "").trim();
  if (!raw) return "";

  // "Teamlio | One connected workspace for growing teams" — the site is the
  // thing, the rest is a strapline that pushes every other row out of view.
  const piped = raw.split("|")[0].trim();
  if (piped && piped !== raw) return piped;

  // host/path: the page is the useful half. The host earns its place only
  // when it says WHERE — a localhost port does not.
  const slash = raw.indexOf("/");
  if (slash > 0) {
    const host = raw.slice(0, slash);
    const page = titleCase(raw.slice(slash + 1).split("/")[0]);
    if (page) {
      return /^localhost(:\d+)?$/i.test(host) ? `${page} (local)` : `${page} — ${host}`;
    }
  }
  return raw;
}

/**
 * Two days of timeline -> what moved, biggest movement first.
 *
 * Sorted by the SIZE of the change, either direction, because an area that got
 * the same four hours on both days is not news — and an area that was four
 * hours yesterday and nothing today is the whole question.
 */
export function compareWorkAreas(
  beforeTimeline: TimelineEntry[] | null | undefined,
  afterTimeline: TimelineEntry[] | null | undefined,
): AreaDelta[] {
  const a = areaMinutes(beforeTimeline);
  const b = areaMinutes(afterTimeline);
  const out: AreaDelta[] = [];
  const areas = new Set([...Array.from(a.keys()), ...Array.from(b.keys())]);
  for (const area of areas) {
    const before = Math.round((a.get(area)?.mins || 0) * 100) / 100;
    const after = Math.round((b.get(area)?.mins || 0) * 100) / 100;
    if (!before && !after) continue;
    const earlierDetails = a.get(area)?.details || new Map();
    const laterDetails = b.get(area)?.details || new Map();
    // `details` keeps its old meaning — the later day where there is one,
    // falling back to the earlier for an area that has since been dropped —
    // so callers that only want "what is it now" are unchanged.
    const details = topDetails(laterDetails.size ? laterDetails : earlierDetails);
    out.push({
      area,
      before,
      after,
      delta: Math.round((after - before) * 100) / 100,
      details,
      beforeDetails: topDetails(earlierDetails),
      afterDetails: topDetails(laterDetails),
    });
  }
  out.sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta) || x.area.localeCompare(y.area));
  return out;
}

export interface AreaRow extends AreaDelta {
  /** both days, only the later day, or only the earlier one. */
  status: "both" | "new" | "stopped";
}

/**
 * The two days as ONE table: a row per work area, a column per day.
 *
 * This replaces three lists above two side-by-side tables. Those showed the
 * same areas twice over — once as "on both days / only today / nothing today"
 * with no files attached, and again per day with the files in grey underneath
 * — and a reader had to hold a name in their head while they looked for it in
 * the other place. One row carries the whole answer: what the area was, what
 * was touched inside it, what each day gave it.
 *
 * Sorted by the LARGER of the two days rather than by the size of the change.
 * The question being asked is "what did each day go on", so the biggest blocks
 * belong at the top; sorting by movement puts a 4m -> 0m row above a 60m ->
 * 55m one, which is true and useless.
 */
/**
 * What a row's change column is actually saying.
 *
 * The distinction that matters: "stopped" and "nothing yet" look identical in
 * the figures and mean opposite things. An area with four hours yesterday and
 * nothing today has been put down — unless today is still running, in which
 * case it simply has not been opened yet, and at 11am that is true of almost
 * everything. The first live version called them all "stopped", in red, on a
 * day that was two hours old.
 *
 * "new" needs no such care: an area that appears today really has appeared,
 * whether or not the day is over.
 */
export function changeKind(
  row: Pick<AreaRow, "status">,
  afterInProgress: boolean,
): "new" | "none-yet" | "stopped" | "delta" {
  if (row.status === "new") return "new";
  if (row.status === "stopped") return afterInProgress ? "none-yet" : "stopped";
  return "delta";
}

export function workAreaRows(
  beforeTimeline: TimelineEntry[] | null | undefined,
  afterTimeline: TimelineEntry[] | null | undefined,
): AreaRow[] {
  return compareWorkAreas(beforeTimeline, afterTimeline)
    .map((d): AreaRow => ({
      ...d,
      status: d.before <= 0 ? "new" : d.after <= 0 ? "stopped" : "both",
    }))
    .sort(
      (x, y) =>
        Math.max(y.before, y.after) - Math.max(x.before, x.after) ||
        x.area.localeCompare(y.area),
    );
}
